import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';

const SAVE_CHAT_MESSAGE = gql`
  mutation SaveChatMessage($conferenceId: ID!, $message: ChatMessageInput!) {
    saveChatMessage(conferenceId: $conferenceId, message: $message) {
      id
      timestamp
    }
  }
`;

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  content: string;
  timestamp: string;
  isOwn?: boolean;
}

interface UseInCallChatOptions {
  conferenceId: string;
  currentUserId: string;
  currentUserName: string;
  audioVideo: any; // AudioVideoFacade from Chime SDK
}

interface UseInCallChatReturn {
  messages: ChatMessage[];
  sendMessage: (content: string) => Promise<void>;
  unreadCount: number;
  clearUnread: () => void;
  isConnected: boolean;
}

const CHAT_TOPIC = 'CHAT';

export function useInCallChat({
  conferenceId,
  currentUserId,
  currentUserName,
  audioVideo,
}: UseInCallChatOptions): UseInCallChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [saveChatMessage] = useMutation(SAVE_CHAT_MESSAGE);
  const isPanelOpenRef = useRef(false);

  // Subscribe to incoming chat messages
  useEffect(() => {
    if (!audioVideo) return;

    const handleDataMessage = (dataMessage: any) => {
      try {
        const data = JSON.parse(dataMessage.text());
        
        if (data.type === 'CHAT') {
          const message: ChatMessage = {
            id: data.id,
            senderId: data.senderId,
            senderName: data.senderName,
            content: data.content,
            timestamp: data.timestamp,
            isOwn: data.senderId === currentUserId,
          };

          setMessages((prev) => {
            // Avoid duplicates
            if (prev.some((m) => m.id === message.id)) {
              return prev;
            }
            return [...prev, message];
          });

          // Increment unread count if not own message and panel is closed
          if (!message.isOwn && !isPanelOpenRef.current) {
            setUnreadCount((prev) => prev + 1);
          }
        }
      } catch (error) {
        console.error('Error parsing chat message:', error);
      }
    };

    try {
      audioVideo.realtimeSubscribeToReceiveDataMessage(CHAT_TOPIC, handleDataMessage);
      setIsConnected(true);
    } catch (error) {
      console.error('Error subscribing to chat messages:', error);
      setIsConnected(false);
    }

    return () => {
      try {
        audioVideo.realtimeUnsubscribeFromReceiveDataMessage(CHAT_TOPIC);
      } catch (error) {
        console.error('Error unsubscribing from chat messages:', error);
      }
    };
  }, [audioVideo, currentUserId]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || !audioVideo) return;

      const message: ChatMessage = {
        id: uuidv4(),
        senderId: currentUserId,
        senderName: currentUserName,
        content: content.trim(),
        timestamp: new Date().toISOString(),
        isOwn: true,
      };

      // Add to local messages immediately
      setMessages((prev) => [...prev, message]);

      // Send via DataMessage for real-time delivery
      try {
        const dataMessage = JSON.stringify({
          type: 'CHAT',
          id: message.id,
          senderId: message.senderId,
          senderName: message.senderName,
          content: message.content,
          timestamp: message.timestamp,
        });

        audioVideo.realtimeSendDataMessage(CHAT_TOPIC, dataMessage);
      } catch (error) {
        console.error('Error sending chat message via DataMessage:', error);
      }

      // Persist to database
      try {
        await saveChatMessage({
          variables: {
            conferenceId,
            message: {
              senderId: message.senderId,
              senderName: message.senderName,
              content: message.content,
              timestamp: message.timestamp,
            },
          },
        });
      } catch (error) {
        console.error('Error persisting chat message:', error);
        // Don't throw - message was still sent in real-time
      }
    },
    [audioVideo, conferenceId, currentUserId, currentUserName, saveChatMessage]
  );

  const clearUnread = useCallback(() => {
    setUnreadCount(0);
    isPanelOpenRef.current = true;
  }, []);

  // Track when panel is closed
  useEffect(() => {
    return () => {
      isPanelOpenRef.current = false;
    };
  }, []);

  return {
    messages,
    sendMessage,
    unreadCount,
    clearUnread,
    isConnected,
  };
}
